/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as GuildSoundboardActionCreators from '@app/actions/GuildSoundboardActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import {Input} from '@app/components/form/Input';
import {SoundboardUploadModal} from '@app/components/modals/SoundboardUploadModal';
import {Slider} from '@app/components/uikit/Slider';
import {Spinner} from '@app/components/uikit/Spinner';
import {Logger} from '@app/lib/Logger';
import GuildStore from '@app/stores/GuildStore';
import PermissionStore from '@app/stores/PermissionStore';
import VoiceSettingsStore from '@app/stores/VoiceSettingsStore';
import {getEmojiURL, getGuildIconURL} from '@app/utils/AvatarUtils';
import {openFilePicker} from '@app/utils/FilePickerUtils';
import {triggerSoundboardSound} from '@app/utils/SoundboardPlayer';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import type {GuildSoundboardSoundWithUser} from '@fluxer/schema/src/domains/guild/GuildSoundboardSchemas';
import {sortBySnowflakeDesc} from '@fluxer/snowflake/src/SnowflakeUtils';
import {useLingui} from '@lingui/react/macro';
import {CaretDownIcon, CaretRightIcon, FunnelIcon, MagnifyingGlassIcon, PlusIcon, SpeakerHighIcon} from '@phosphor-icons/react';
import {matchSorter} from 'match-sorter';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const logger = new Logger('SoundboardPopout');

const COLLAPSE_STORAGE_KEY = 'fluxer.soundboard.collapsedServers';

type PopoutSound = GuildSoundboardSoundWithUser & {guildId: string; guildName: string};

interface ServerGroup {
	guildId: string;
	guildName: string;
	sounds: Array<PopoutSound>;
}

function loadCollapsedServers(): Set<string> {
	try {
		const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
		if (raw) {
			const parsed = JSON.parse(raw);
			if (Array.isArray(parsed)) {
				return new Set(parsed.filter((v): v is string => typeof v === 'string'));
			}
		}
	} catch {}
	return new Set();
}

function saveCollapsedServers(set: Set<string>): void {
	try {
		localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify([...set]));
	} catch {}
}

function groupByCategoryEntries(sounds: ReadonlyArray<PopoutSound>): Array<[string, Array<PopoutSound>]> {
	const map = new Map<string, Array<PopoutSound>>();
	for (const sound of sounds) {
		const key = sound.category ?? ' ';
		const arr = map.get(key) ?? [];
		arr.push(sound);
		map.set(key, arr);
	}
	return [...map.entries()].sort(([a], [b]) => (a === ' ' ? 1 : b === ' ' ? -1 : a.localeCompare(b)));
}

export const SoundboardPopout = observer(function SoundboardPopout({
	guildId,
	onClose,
}: {
	guildId: string;
	onClose: () => void;
}) {
	const {t} = useLingui();
	const [sounds, setSounds] = useState<ReadonlyArray<PopoutSound>>([]);
	const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
	const [search, setSearch] = useState('');
	const [showFilters, setShowFilters] = useState(false);
	const [showVolume, setShowVolume] = useState(false);
	const [groupByServer, setGroupByServer] = useState(true);
	const [groupByCategory, setGroupByCategory] = useState(true);
	const [collapsedServers, setCollapsedServers] = useState<Set<string>>(() => loadCollapsedServers());

	const sectionRefs = useRef(new Map<string, HTMLDivElement | null>());

	const canManage = PermissionStore.can(Permissions.MANAGE_GUILD, {guildId});

	const fetchSounds = useCallback(async () => {
		try {
			setStatus('pending');
			const guilds = GuildStore.getGuilds();
			const results = await Promise.allSettled(
				guilds.map(async (guild) => {
					const list = await GuildSoundboardActionCreators.list(guild.id);
					return list.map((s) => ({...s, guildId: guild.id, guildName: guild.name}));
				}),
			);
			const flat: Array<PopoutSound> = [];
			for (const r of results) {
				if (r.status === 'fulfilled') {
					flat.push(...r.value);
				}
			}
			setSounds(sortBySnowflakeDesc(flat) as Array<PopoutSound>);
			setStatus('success');
		} catch (error) {
			logger.error('Failed to fetch soundboard sounds', error);
			setStatus('error');
		}
	}, []);

	useEffect(() => {
		void fetchSounds();
	}, [fetchSounds]);

	const existingCategories = useMemo(() => {
		const set = new Set<string>();
		for (const sound of sounds) {
			if (sound.guildId === guildId && sound.category) {
				set.add(sound.category);
			}
		}
		return [...set].sort((a, b) => a.localeCompare(b));
	}, [sounds, guildId]);

	const filtered = useMemo(() => {
		if (!search) return sounds;
		return matchSorter(sounds, search, {keys: [(s) => s.name, (s) => s.category ?? '', (s) => s.guildName]});
	}, [sounds, search]);

	const serverGroups = useMemo<Array<ServerGroup>>(() => {
		const map = new Map<string, ServerGroup>();
		for (const sound of filtered) {
			let group = map.get(sound.guildId);
			if (!group) {
				group = {guildId: sound.guildId, guildName: sound.guildName, sounds: []};
				map.set(sound.guildId, group);
			}
			group.sounds.push(sound);
		}
		return [...map.values()].sort((a, b) => {
			if (a.guildId === guildId) return -1;
			if (b.guildId === guildId) return 1;
			return a.guildName.localeCompare(b.guildName);
		});
	}, [filtered, guildId]);

	const showServerNav = groupByServer && serverGroups.length > 1;

	const toggleServer = useCallback((gid: string) => {
		setCollapsedServers((prev) => {
			const next = new Set(prev);
			if (next.has(gid)) {
				next.delete(gid);
			} else {
				next.add(gid);
			}
			saveCollapsedServers(next);
			return next;
		});
	}, []);

	const handleNavToServer = useCallback((gid: string) => {
		setCollapsedServers((prev) => {
			if (!prev.has(gid)) return prev;
			const next = new Set(prev);
			next.delete(gid);
			saveCollapsedServers(next);
			return next;
		});
		sectionRefs.current.get(gid)?.scrollIntoView({behavior: 'smooth', block: 'start'});
	}, []);

	const handleUpload = useCallback(async () => {
		const [file] = await openFilePicker({accept: 'audio/*'});
		if (file) {
			ModalActionCreators.push(
				modal(() => (
					<SoundboardUploadModal
						guildId={guildId}
						file={file}
						existingCategories={existingCategories}
						onSuccess={fetchSounds}
					/>
				)),
			);
		}
	}, [guildId, existingCategories, fetchSounds]);

	const handlePlay = useCallback((sound: PopoutSound) => {
		void triggerSoundboardSound({soundId: sound.id, guildId: sound.guildId, volume: sound.volume});
	}, []);

	const sectionLabelStyle: React.CSSProperties = {
		fontSize: '0.7rem',
		fontWeight: 700,
		textTransform: 'uppercase',
		color: 'var(--text-primary-muted)',
	};

	const renderGrid = useCallback(
		(items: ReadonlyArray<PopoutSound>) => (
			<div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: '0.4rem'}}>
				{items.map((sound) => (
					<button
						key={`${sound.guildId}:${sound.id}`}
						type="button"
						onClick={() => handlePlay(sound)}
						title={sound.name}
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: '0.2rem',
							padding: '0.5rem 0.25rem',
							borderRadius: '0.5rem',
							border: 'none',
							cursor: 'pointer',
							background: 'var(--background-secondary)',
							color: 'var(--text-primary)',
						}}
					>
						<span style={{fontSize: '1.5rem', lineHeight: 1}}>
							{sound.emoji_id ? (
								<img src={getEmojiURL({id: sound.emoji_id})} alt="" width={24} height={24} />
							) : (
								(sound.emoji_name ?? '🔊')
							)}
						</span>
						<span
							style={{
								fontSize: '0.7rem',
								width: '100%',
								textAlign: 'center',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{sound.name}
						</span>
					</button>
				))}
			</div>
		),
		[handlePlay],
	);

	const renderCategoryGroups = useCallback(
		(items: ReadonlyArray<PopoutSound>) =>
			groupByCategoryEntries(items).map(([category, catSounds]) => (
				<div key={category} style={{display: 'flex', flexDirection: 'column', gap: '0.35rem'}}>
					<span style={sectionLabelStyle}>{category === ' ' ? t`Uncategorized` : category}</span>
					{renderGrid(catSounds)}
				</div>
			)),
		[renderGrid, t],
	);

	const renderServerSounds = useCallback(
		(items: ReadonlyArray<PopoutSound>) => (groupByCategory ? renderCategoryGroups(items) : renderGrid(items)),
		[groupByCategory, renderCategoryGroups, renderGrid],
	);

	const renderBody = () => {
		if (status === 'pending') {
			return (
				<div style={{display: 'flex', justifyContent: 'center', padding: '1rem 0'}}>
					<Spinner />
				</div>
			);
		}
		if (status === 'error') {
			return (
				<span style={{color: 'var(--text-primary-muted)', textAlign: 'center', padding: '1rem 0'}}>
					{t`Failed to load sounds.`}
				</span>
			);
		}
		if (sounds.length === 0) {
			return (
				<span style={{color: 'var(--text-primary-muted)', textAlign: 'center', padding: '1rem 0'}}>
					{t`No sounds yet.`}
				</span>
			);
		}

		if (showServerNav) {
			return serverGroups.map((group) => {
				const collapsed = collapsedServers.has(group.guildId);
				return (
					<div
						key={group.guildId}
						ref={(el) => {
							sectionRefs.current.set(group.guildId, el);
						}}
						style={{display: 'flex', flexDirection: 'column', gap: '0.4rem'}}
					>
						<button
							type="button"
							onClick={() => toggleServer(group.guildId)}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '0.3rem',
								border: 'none',
								background: 'transparent',
								cursor: 'pointer',
								padding: '0.1rem 0',
								color: 'var(--text-primary)',
								fontWeight: 700,
								fontSize: '0.8rem',
								textTransform: 'uppercase',
							}}
						>
							{collapsed ? <CaretRightIcon size={12} weight="bold" /> : <CaretDownIcon size={12} weight="bold" />}
							<span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
								{group.guildName}
							</span>
						</button>
						{!collapsed && renderServerSounds(group.sounds)}
					</div>
				);
			});
		}

		if (groupByCategory) {
			return renderCategoryGroups(filtered);
		}
		return renderGrid(filtered);
	};

	const checkboxRowStyle: React.CSSProperties = {
		display: 'flex',
		alignItems: 'center',
		gap: '0.4rem',
		fontSize: '0.8rem',
		color: 'var(--text-primary)',
		cursor: 'pointer',
	};

	const headerButtonStyle = (active: boolean): React.CSSProperties => ({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '1.75rem',
		height: '1.75rem',
		borderRadius: '0.375rem',
		border: 'none',
		cursor: 'pointer',
		background: active ? 'var(--brand-primary, #5865f2)' : 'var(--background-tertiary)',
		color: 'var(--text-primary)',
	});

	return (
		<div
			style={{
				display: 'flex',
				gap: '0.5rem',
				width: showServerNav ? '388px' : '340px',
				maxHeight: '440px',
				padding: '0.75rem',
				borderRadius: '0.5rem',
				background: 'var(--background-floating, var(--background-secondary))',
				boxShadow: 'var(--elevation-high, 0 8px 16px rgba(0, 0, 0, 0.24))',
			}}
		>
			{showServerNav && (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '0.35rem',
						overflowY: 'auto',
						paddingRight: '0.4rem',
						borderRight: '1px solid var(--background-header-secondary)',
					}}
				>
					{serverGroups.map((group) => {
						const guild = GuildStore.getGuild(group.guildId);
						const iconUrl = guild?.icon ? getGuildIconURL({id: group.guildId, icon: guild.icon}) : null;
						return (
							<button
								key={group.guildId}
								type="button"
								onClick={() => handleNavToServer(group.guildId)}
								title={group.guildName}
								aria-label={group.guildName}
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: '2rem',
									height: '2rem',
									borderRadius: '50%',
									border: 'none',
									cursor: 'pointer',
									overflow: 'hidden',
									background: 'var(--background-tertiary)',
									color: 'var(--text-primary)',
									fontWeight: 700,
									fontSize: '0.8rem',
									flexShrink: 0,
								}}
							>
								{iconUrl ? (
									<img src={iconUrl} alt="" width={32} height={32} style={{width: '100%', height: '100%'}} />
								) : (
									group.guildName.charAt(0).toUpperCase()
								)}
							</button>
						);
					})}
				</div>
			)}

			<div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1, minWidth: 0}}>
				<div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem'}}>
					<span style={{fontWeight: 700, color: 'var(--text-primary)'}}>{t`Soundboard`}</span>
					<div style={{display: 'flex', gap: '0.35rem'}}>
						<button
							type="button"
							onClick={() => setShowVolume((v) => !v)}
							aria-label={t`Volume`}
							style={headerButtonStyle(showVolume)}
						>
							<SpeakerHighIcon size={16} weight={showVolume ? 'fill' : 'regular'} />
						</button>
						<button
							type="button"
							onClick={() => setShowFilters((v) => !v)}
							aria-label={t`Filters`}
							style={headerButtonStyle(showFilters)}
						>
							<FunnelIcon size={16} weight={showFilters ? 'fill' : 'regular'} />
						</button>
						{canManage && (
							<button
								type="button"
								onClick={handleUpload}
								aria-label={t`Upload Sound`}
								style={headerButtonStyle(false)}
							>
								<PlusIcon size={16} weight="bold" />
							</button>
						)}
					</div>
				</div>

				<Input
					type="text"
					placeholder={t`Search...`}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					leftIcon={<MagnifyingGlassIcon size={14} weight="bold" />}
				/>

				{showFilters && (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '0.3rem',
							padding: '0.5rem',
							borderRadius: '0.375rem',
							background: 'var(--background-secondary)',
						}}
					>
						<label style={checkboxRowStyle}>
							<input type="checkbox" checked={groupByServer} onChange={(e) => setGroupByServer(e.target.checked)} />
							{t`Group by server`}
						</label>
						<label style={checkboxRowStyle}>
							<input
								type="checkbox"
								checked={groupByCategory}
								onChange={(e) => setGroupByCategory(e.target.checked)}
							/>
							{t`Group by category`}
						</label>
					</div>
				)}

				{showVolume && (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '0.4rem',
							padding: '0.5rem 0.6rem',
							borderRadius: '0.375rem',
							background: 'var(--background-secondary)',
						}}
					>
						<span style={{fontSize: '0.8rem', color: 'var(--text-primary)'}}>
							{t`Volume`}: {Math.round(VoiceSettingsStore.getSoundboardVolume() * 100)}%
						</span>
						<Slider
							minValue={0}
							maxValue={100}
							defaultValue={Math.round(VoiceSettingsStore.getSoundboardVolume() * 100)}
							factoryDefaultValue={100}
							onValueChange={(v) => VoiceSettingsStore.setSoundboardVolume(v / 100)}
							asValueChanges={(v) => VoiceSettingsStore.setSoundboardVolume(v / 100)}
						/>
					</div>
				)}

				<div style={{overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.6rem'}}>{renderBody()}</div>
			</div>
		</div>
	);
});
